import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  Badge,
  Box,
  Button,
  Container,
  Divider,
  Drawer,
  Group,
  List,
  Loader,
  Modal,
  PasswordInput,
  Select,
  MultiSelect,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconPlus, IconRefresh, IconSearch, IconTrash, IconUserCheck } from '@tabler/icons-react'
import type { UserAuditEventDto, UserDto } from '@ledokol/contracts'
import { AppLayout } from '../features/projects/app-shell'
import { useAuthStore } from '../features/auth/store'
import { createUser, deleteUser, fetchUserAudit, fetchUsers, promoteUser, updateUser } from '../shared/api/users'
import { fetchDepartments, type DepartmentRow } from '../shared/api/departments'
import { ResetPasswordSection } from '../features/users/reset-password-section'

const ROLE_OPTS = [
  { value: 'admin', label: 'admin' },
  { value: 'director', label: 'director' },
  { value: 'head', label: 'head' },
  { value: 'account', label: 'account' },
  { value: 'employee', label: 'employee' },
  { value: 'observer', label: 'observer' },
]
const LEVEL_OPTS = [
  { value: 'junior', label: 'junior' },
  { value: 'middle', label: 'middle' },
  { value: 'senior', label: 'senior' },
  { value: 'lead', label: 'lead' },
]

export function AdminUsersPage() {
  const { user } = useAuthStore()
  const [params, setParams] = useSearchParams()

  const [users, setUsers] = useState<UserDto[]>([])
  const [departments, setDepartments] = useState<DepartmentRow[]>([])
  const [loading, setLoading] = useState(true)

  // фильтры
  const departmentId = params.get('department') ?? ''
  const role = params.get('role') ?? ''
  const onlyStubs = params.get('stubs') === 'true'
  const active = params.get('active') ?? '' // '', 'true', 'false'
  const search = params.get('q') ?? ''

  // редактирование
  const [editing, setEditing] = useState<UserDto | null>(null)
  const [promoting, setPromoting] = useState<UserDto | null>(null)
  const [creating, setCreating] = useState(false)

  function setFilter(k: string, v: string | null) {
    const next = new URLSearchParams(params)
    if (v) next.set(k, v)
    else next.delete(k)
    setParams(next, { replace: true })
  }

  useEffect(() => {
    fetchDepartments()
      .then(setDepartments)
      .catch(() =>
        notifications.show({
          color: 'red',
          title: 'Ошибка',
          message: 'Не удалось загрузить отделы — фильтр по отделу будет пуст',
        }),
      )
  }, [])

  async function reload() {
    setLoading(true)
    try {
      const list = await fetchUsers({
        department_id: departmentId || undefined,
        role: role || undefined,
        is_stub: onlyStubs ? true : undefined,
        active: active === '' ? undefined : active === 'true',
        search: search || undefined,
      })
      setUsers(list)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentId, role, onlyStubs, active, search])

  if (!user) return null
  if (user.role !== 'admin') {
    return (
      <AppLayout>
        <Container>
          <Text c="red" mt="md">Доступ только для admin.</Text>
        </Container>
      </AppLayout>
    )
  }

  return (
    <AppLayout>
      <Container size="xl" py="md">
        <Group justify="space-between" mb="md">
          <Title order={3}>Сотрудники</Title>
          <Group gap="xs">
            <Button leftSection={<IconPlus size={16} />} onClick={() => setCreating(true)}>
              Создать сотрудника
            </Button>
            <Button leftSection={<IconRefresh size={16} />} variant="subtle" onClick={() => void reload()}>
              Обновить
            </Button>
          </Group>
        </Group>

        <Group mb="md" gap="xs" wrap="wrap">
          <TextInput
            placeholder="Поиск по имени/email"
            leftSection={<IconSearch size={14} />}
            value={search}
            onChange={(e) => setFilter('q', e.currentTarget.value || null)}
            w={260}
          />
          <Select
            placeholder="Отдел"
            data={[{ value: '', label: 'Все отделы' }, ...departments.map((d) => ({ value: d.id, label: d.name }))]}
            value={departmentId}
            onChange={(v) => setFilter('department', v)}
            clearable
            w={200}
          />
          <Select placeholder="Роль" data={[{ value: '', label: 'Все роли' }, ...ROLE_OPTS]} value={role} onChange={(v) => setFilter('role', v)} clearable w={160} />
          <Select
            placeholder="Активность"
            data={[{ value: '', label: 'Все' }, { value: 'true', label: 'Активные' }, { value: 'false', label: 'Деактивированные' }]}
            value={active}
            onChange={(v) => setFilter('active', v)}
            clearable
            w={170}
          />
          <Button variant={onlyStubs ? 'filled' : 'light'} onClick={() => setFilter('stubs', onlyStubs ? null : 'true')}>
            Только стабы
          </Button>
        </Group>

        {loading ? (
          <Group justify="center" p="xl"><Loader /></Group>
        ) : (
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Имя</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th>@TG</Table.Th>
                <Table.Th>Роль</Table.Th>
                <Table.Th>Отдел</Table.Th>
                <Table.Th>Уровень</Table.Th>
                <Table.Th>Активен</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {users.map((u) => (
                <Table.Tr key={u.id} style={{ cursor: 'pointer' }} onClick={() => setEditing(u)}>
                  <Table.Td>
                    <Group gap="xs">
                      <Text size="sm">{u.full_name}</Text>
                      {u.is_stub && <Badge size="xs" color="orange" variant="light">стаб</Badge>}
                    </Group>
                  </Table.Td>
                  <Table.Td><Text size="xs" c={u.is_stub ? 'dimmed' : undefined}>{u.email}</Text></Table.Td>
                  <Table.Td><Text size="xs">{u.tg_username ? `@${u.tg_username}` : '—'}</Text></Table.Td>
                  <Table.Td><Badge size="sm" variant="light">{u.role}</Badge></Table.Td>
                  <Table.Td>{u.departments.map((d) => d.name).join(', ') || '—'}</Table.Td>
                  <Table.Td>{u.staff_level ?? '—'}</Table.Td>
                  <Table.Td>{u.is_active ? '✓' : '✗'}</Table.Td>
                  <Table.Td>
                    {u.is_stub && (
                      <Button size="xs" leftSection={<IconUserCheck size={14} />} onClick={(e) => { e.stopPropagation(); setPromoting(u) }}>
                        Промоут
                      </Button>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
              {users.length === 0 && (
                <Table.Tr><Table.Td colSpan={8}><Text c="dimmed" ta="center" py="md">Никого не найдено по фильтрам.</Text></Table.Td></Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        )}

        <EditUserDrawer user={editing} departments={departments} currentUserId={user?.id ?? null} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); void reload() }} onRefresh={() => void reload()} />
        <PromoteUserDrawer user={promoting} departments={departments} onClose={() => setPromoting(null)} onSaved={() => { setPromoting(null); void reload() }} />
        <CreateUserDrawer opened={creating} departments={departments} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); void reload() }} />
      </Container>
    </AppLayout>
  )
}

function CreateUserDrawer({ opened, departments, onClose, onSaved }: { opened: boolean; departments: DepartmentRow[]; onClose: () => void; onSaved: () => void }) {
  const [email, setEmail] = useState('')
  const [fullName, setFullName] = useState('')
  const [role, setRole] = useState<string | null>('employee')
  const [deptIds, setDeptIds] = useState<string[]>([])
  const [password, setPassword] = useState('')
  const [tgUsername, setTgUsername] = useState('')
  const [level, setLevel] = useState<string | null>(null)
  const [staffRole, setStaffRole] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (opened) {
      setEmail(''); setFullName(''); setRole('employee'); setDeptIds([])
      setPassword(''); setTgUsername(''); setLevel(null); setStaffRole('')
    }
  }, [opened])

  // head обязан иметь отдел (data-model инвариант)
  const headNeedsDept = role === 'head' && deptIds.length === 0
  const canSave = useMemo(
    () => email.includes('@') && fullName.length >= 1 && !!role && password.length >= 8 && !headNeedsDept,
    [email, fullName, role, password, headNeedsDept],
  )

  async function save() {
    if (!role) return
    setSaving(true)
    try {
      await createUser({
        email,
        full_name: fullName,
        role: role as 'admin' | 'director' | 'head' | 'account' | 'employee' | 'observer',
        department_ids: deptIds,
        initial_password: password,
        tg_username: tgUsername || undefined,
        staff_level: (level as 'junior' | 'middle' | 'senior' | 'lead') || undefined,
        staff_role: staffRole || undefined,
      })
      notifications.show({ message: 'Сотрудник создан', color: 'green' })
      onSaved()
    } catch (e) {
      notifications.show({ message: extractError(e), color: 'red' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Drawer opened={opened} onClose={onClose} title="Создание сотрудника" position="right" size="md">
      <Stack>
        <TextInput label="Полное имя" required placeholder="Иван Иванов" value={fullName} onChange={(e) => setFullName(e.currentTarget.value)} />
        <TextInput label="Email" required placeholder="ivan@ledokol.uz" value={email} onChange={(e) => setEmail(e.currentTarget.value)} />
        <Select label="Роль (RBAC)" required data={ROLE_OPTS} value={role} onChange={setRole} />
        <MultiSelect
          label={`Отдел(ы)${role === 'head' ? ' (обяз. для head)' : ''}`}
          required={role === 'head'}
          data={departments.map((d) => ({ value: d.id, label: d.name }))}
          value={deptIds}
          onChange={setDeptIds}
          clearable
        />
        <PasswordInput label="Initial password" required description="Минимум 8 символов" value={password} onChange={(e) => setPassword(e.currentTarget.value)} />
        <TextInput
          label="@TG username (опц.)"
          placeholder="vasya  — без @"
          description="Если стаб с этим handle уже есть в БД, его след автоматически переедет к новому (merge)."
          value={tgUsername}
          onChange={(e) => setTgUsername(e.currentTarget.value.replace(/^@/, ''))}
        />
        <Select label="Уровень (staff_level)" data={[{ value: '', label: '— нет —' }, ...LEVEL_OPTS]} value={level ?? ''} onChange={(v) => setLevel(v || null)} clearable />
        <TextInput label="Функц. роль (staff_role)" placeholder="Performance / SMM / PR" value={staffRole} onChange={(e) => setStaffRole(e.currentTarget.value)} />
        {headNeedsDept && <Text size="xs" c="red">Для роли «head» обязательно указать отдел.</Text>}
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>Отмена</Button>
          <Button loading={saving} disabled={!canSave} onClick={save}>Создать</Button>
        </Group>
      </Stack>
    </Drawer>
  )
}

function EditUserDrawer({ user, departments, currentUserId, onClose, onSaved, onRefresh }: { user: UserDto | null; departments: DepartmentRow[]; currentUserId: string | null; onClose: () => void; onSaved: () => void; onRefresh: () => void }) {
  const [role, setRole] = useState<string | null>(null)
  const [deptIds, setDeptIds] = useState<string[]>([])
  const [level, setLevel] = useState<string | null>(null)
  const [staffRole, setStaffRole] = useState('')
  const [active, setActive] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [audit, setAudit] = useState<UserAuditEventDto[]>([])
  const [auditLoading, setAuditLoading] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  // Когда бэк говорит «нельзя удалить — есть активные ссылки», показываем блокирующую
  // модалку со списком, чтобы admin пошёл и переназначил/закрыл (а не упёрся в тост).
  const [blocked, setBlocked] = useState<DeleteBlock | null>(null)

  const isSelf = !!user && user.id === currentUserId
  const actor = useAuthStore((s) => s.user)
  // Сервер для не-admin отказывает в сбросе admin/director — не показываем заведомо 403-кнопку.
  const canResetPassword =
    !!actor?.permissions.includes('users.reset_password') &&
    (actor.role === 'admin' || (user?.role !== 'admin' && user?.role !== 'director'))
  // после сброса в этой сессии карточки: user — снимок из таблицы, флаг в нём устарел
  const [resetDone, setResetDone] = useState(false)

  function loadAudit(id: string) {
    setAuditLoading(true)
    fetchUserAudit(id)
      .then(setAudit)
      .catch(() => setAudit([]))
      .finally(() => setAuditLoading(false))
  }

  async function doDelete() {
    if (!user) return
    setDeleting(true)
    try {
      await deleteUser(user.id)
      notifications.show({ message: `${user.full_name} удалён`, color: 'green' })
      setConfirmDelete(false)
      onSaved()
    } catch (e) {
      const block = extractDeleteBlock(e)
      if (block) {
        setConfirmDelete(false)
        setBlocked(block)
      } else {
        notifications.show({ message: extractError(e), color: 'red' })
      }
    } finally {
      setDeleting(false)
    }
  }

  useEffect(() => {
    if (user) {
      setRole(user.role)
      setDeptIds(user.departments.map((d) => d.id))
      setLevel(user.staff_level ?? null)
      setStaffRole(user.staff_role ?? '')
      setActive(user.is_active ? 'true' : 'false')
      setResetDone(false)
      loadAudit(user.id)
    } else {
      setAudit([])
    }
  }, [user])

  async function save() {
    if (!user) return
    setSaving(true)
    try {
      await updateUser(user.id, {
        role: (role as 'admin' | 'director' | 'head' | 'account' | 'employee' | 'observer') ?? undefined,
        department_ids: deptIds,
        staff_level: (level as 'junior' | 'middle' | 'senior' | 'lead' | null) ?? null,
        staff_role: staffRole || null,
        is_active: active === 'true',
      })
      notifications.show({ message: 'Сохранено', color: 'green' })
      onSaved()
    } catch (e) {
      notifications.show({ message: extractError(e), color: 'red' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Drawer opened={!!user} onClose={onClose} title={user ? `Редактирование: ${user.full_name}` : ''} position="right" size="md">
      {user && (
        <Stack>
          <Box>
            <Text size="xs" c="dimmed">Email</Text>
            <Text size="sm">{user.email}</Text>
          </Box>
          <Box>
            <Text size="xs" c="dimmed">TG</Text>
            <Text size="sm">{user.tg_username ? `@${user.tg_username}` : '—'}</Text>
          </Box>
          <Select label="Роль (RBAC)" data={ROLE_OPTS} value={role} onChange={setRole} />
          <MultiSelect
            label="Отделы"
            data={departments.map((d) => ({ value: d.id, label: d.name }))}
            value={deptIds}
            onChange={setDeptIds}
            clearable
          />
          <Select label="Уровень (staff_level)" data={[{ value: '', label: '— нет —' }, ...LEVEL_OPTS]} value={level ?? ''} onChange={(v) => setLevel(v || null)} clearable />
          <TextInput label="Функц. роль (staff_role)" placeholder="например, Performance / SMM / PR" value={staffRole} onChange={(e) => setStaffRole(e.currentTarget.value)} />
          <Select label="Активность" data={[{ value: 'true', label: 'Активен' }, { value: 'false', label: 'Деактивирован' }]} value={active} onChange={setActive} />
          {canResetPassword && !isSelf && !user.is_stub && (
            <Group>
              <ResetPasswordSection user={user} onReset={() => { setResetDone(true); loadAudit(user.id); onRefresh() }} />
              {(user.must_change_password || resetDone) && <Badge color="orange" variant="light">ждёт смены пароля</Badge>}
            </Group>
          )}
          <Group justify="space-between">
            <Button
              variant="subtle"
              color="red"
              leftSection={<IconTrash size={16} />}
              onClick={() => setConfirmDelete(true)}
              disabled={isSelf}
              title={isSelf ? 'Нельзя удалить самого себя' : 'Удалить сотрудника'}
            >
              Удалить
            </Button>
            <Group>
              <Button variant="subtle" onClick={onClose}>Отмена</Button>
              <Button loading={saving} onClick={save}>Сохранить</Button>
            </Group>
          </Group>

          <Modal
            opened={confirmDelete}
            onClose={() => setConfirmDelete(false)}
            title={`Удалить «${user.full_name}»?`}
            centered
            size="md"
          >
            <Stack>
              <Text size="sm">Что произойдёт:</Text>
              <List size="sm" spacing="xs">
                <List.Item>Сотрудник пропадёт из всех списков и фильтров.</List.Item>
                <List.Item>Связь с TG-аккаунтом обрывается — он сможет заново привязаться к другому пользователю.</List.Item>
                <List.Item>
                  Членство в командах проектов — <Text component="span" fw={600}>стирается</Text>;
                  если он был исполнителем задач — задачи становятся «без исполнителя».
                </List.Item>
                <List.Item>
                  Его имя в истории комментариев и аудите <Text component="span" fw={600}>сохранится</Text>
                  как «(удалён)».
                </List.Item>
                <List.Item>
                  Если он держит лид/reporter активного проекта или задачи — удаление будет
                  <Text component="span" fw={600}> заблокировано</Text>, нужно сначала переназначить.
                </List.Item>
              </List>
              <Divider my="xs" />
              <Group justify="flex-end">
                <Button variant="subtle" onClick={() => setConfirmDelete(false)}>Отмена</Button>
                <Button color="red" loading={deleting} onClick={doDelete}>Удалить</Button>
              </Group>
            </Stack>
          </Modal>

          <Modal
            opened={!!blocked}
            onClose={() => setBlocked(null)}
            title={`Нельзя удалить «${user.full_name}»`}
            centered
            size="lg"
          >
            {blocked && (
              <Stack>
                <Text size="sm">
                  Сотрудник держит активные роли. Сначала переназначьте их на другого, потом удалите.
                </Text>
                {blocked.lead_of_projects.length > 0 && (
                  <Box>
                    <Text size="sm" fw={600} mb={4}>
                      Лид {blocked.lead_of_projects.length} активн{ruEnding(blocked.lead_of_projects.length, 'ого', 'ых', 'ых')} проект{ruEnding(blocked.lead_of_projects.length, 'а', 'ов', 'ов')}:
                    </Text>
                    <List size="sm" spacing={2}>
                      {blocked.lead_of_projects.map((p) => (
                        <List.Item key={p.id}>
                          <Link to={`/projects/${p.id}/details`} target="_blank" rel="noreferrer">{p.name}</Link>
                        </List.Item>
                      ))}
                    </List>
                  </Box>
                )}
                {blocked.creator_of_projects.length > 0 && (
                  <Box>
                    <Text size="sm" fw={600} mb={4}>
                      Создатель {blocked.creator_of_projects.length} активн{ruEnding(blocked.creator_of_projects.length, 'ого', 'ых', 'ых')} проект{ruEnding(blocked.creator_of_projects.length, 'а', 'ов', 'ов')}:
                    </Text>
                    <List size="sm" spacing={2}>
                      {blocked.creator_of_projects.map((p) => (
                        <List.Item key={p.id}>
                          <Link to={`/projects/${p.id}/details`} target="_blank" rel="noreferrer">{p.name}</Link>
                        </List.Item>
                      ))}
                    </List>
                    <Text size="xs" c="dimmed" mt={2}>
                      created_by — поле служебное; можно изменить через SQL (admin), либо переназначьте лидом и закройте проект.
                    </Text>
                  </Box>
                )}
                {blocked.reporter_of_tasks.length > 0 && (
                  <Box>
                    <Text size="sm" fw={600} mb={4}>
                      Reporter {blocked.reporter_of_tasks.length} активн{ruEnding(blocked.reporter_of_tasks.length, 'ой', 'ых', 'ых')} задач{ruEnding(blocked.reporter_of_tasks.length, 'и', '', '')}:
                    </Text>
                    <List size="sm" spacing={2}>
                      {blocked.reporter_of_tasks.map((t) => (
                        <List.Item key={t.id}>
                          <Link to={`/projects/${t.project_id}?task=${t.id}`} target="_blank" rel="noreferrer">{t.title}</Link>
                        </List.Item>
                      ))}
                    </List>
                  </Box>
                )}
                <Divider my="xs" />
                <Group justify="flex-end">
                  <Button onClick={() => setBlocked(null)}>Понятно</Button>
                </Group>
              </Stack>
            )}
          </Modal>

          <Box mt="md">
            <Text size="sm" fw={600} mb="xs">История изменений</Text>
            {auditLoading ? (
              <Loader size="xs" />
            ) : audit.length === 0 ? (
              <Text size="xs" c="dimmed">Пока пусто.</Text>
            ) : (
              <Stack gap={4}>
                {audit.map((e) => (
                  <Box key={e.id} p="xs" style={{ borderLeft: '2px solid var(--mantine-color-blue-3)', background: 'var(--mantine-color-gray-0)' }}>
                    <Group gap={6}>
                      <Badge size="xs" variant="light" color={badgeColor(e.event_type)}>{e.event_type}</Badge>
                      <Text size="xs" c="dimmed">{new Date(e.created_at).toLocaleString('ru-RU')}</Text>
                      <Text size="xs" c="dimmed">· {e.actor?.full_name ?? 'система'}</Text>
                    </Group>
                    {(e.from_value != null || e.to_value != null) && (
                      <Text size="xs" c="dimmed" style={{ fontFamily: 'monospace', marginTop: 2 }}>
                        {e.from_value != null ? `${JSON.stringify(e.from_value)} → ` : ''}
                        {e.to_value != null ? JSON.stringify(e.to_value) : ''}
                      </Text>
                    )}
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        </Stack>
      )}
    </Drawer>
  )
}

function badgeColor(t: string): string {
  switch (t) {
    case 'promoted': return 'green'
    case 'role_changed': return 'blue'
    case 'department_changed': return 'cyan'
    case 'activated': return 'green'
    case 'deactivated': return 'red'
    case 'staff_changed': return 'grape'
    case 'password_reset': return 'orange'
    case 'password_changed': return 'teal'
    default: return 'gray'
  }
}

function PromoteUserDrawer({ user, departments, onClose, onSaved }: { user: UserDto | null; departments: DepartmentRow[]; onClose: () => void; onSaved: () => void }) {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<string | null>('employee')
  const [deptIds, setDeptIds] = useState<string[]>([])
  const [level, setLevel] = useState<string | null>(null)
  const [staffRole, setStaffRole] = useState('')
  const [password, setPassword] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (user) {
      setEmail('')
      setRole('employee')
      setDeptIds([])
      setLevel(null)
      setStaffRole('')
      setPassword('')
    }
  }, [user])

  const canSave = useMemo(() => !!user && email.includes('@') && !!role && deptIds.length > 0, [user, email, role, deptIds])

  async function save() {
    if (!user || !role || deptIds.length === 0) return
    setSaving(true)
    try {
      await promoteUser(user.id, {
        email,
        role: role as 'admin' | 'director' | 'head' | 'account' | 'employee' | 'observer',
        department_ids: deptIds,
        staff_level: (level as 'junior' | 'middle' | 'senior' | 'lead') || undefined,
        staff_role: staffRole || undefined,
        initial_password: password || undefined,
      })
      notifications.show({ message: 'Стаб промоутирован', color: 'green' })
      onSaved()
    } catch (e) {
      notifications.show({ message: extractError(e), color: 'red' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Drawer opened={!!user} onClose={onClose} title={user ? `Промоут стаба: ${user.full_name}` : ''} position="right" size="md">
      {user && (
        <Stack>
          <Box>
            <Text size="xs" c="dimmed">TG</Text>
            <Text size="sm">{user.tg_username ? `@${user.tg_username}` : '—'} · tg_user_id {user.tg_user_id ?? '—'}</Text>
          </Box>
          <TextInput label="Email (реальный)" placeholder="ivan@ledokol.uz" required value={email} onChange={(e) => setEmail(e.currentTarget.value)} />
          <Select label="Роль (RBAC)" required data={ROLE_OPTS} value={role} onChange={setRole} />
          <MultiSelect
            label="Отделы"
            required
            data={departments.map((d) => ({ value: d.id, label: d.name }))}
            value={deptIds}
            onChange={setDeptIds}
          />
          <Select label="Уровень (staff_level)" data={[{ value: '', label: '— нет —' }, ...LEVEL_OPTS]} value={level ?? ''} onChange={(v) => setLevel(v || null)} clearable />
          <TextInput label="Функц. роль" placeholder="например, Performance" value={staffRole} onChange={(e) => setStaffRole(e.currentTarget.value)} />
          <PasswordInput label="Initial password (опц.)" description="Без пароля — войти можно будет через будущий TG-вход / SSO" value={password} onChange={(e) => setPassword(e.currentTarget.value)} />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={onClose}>Отмена</Button>
            <Button loading={saving} disabled={!canSave} onClick={save}>Промоутить</Button>
          </Group>
        </Stack>
      )}
    </Drawer>
  )
}

function extractError(e: unknown): string {
  const err = e as { response?: { data?: { error?: { message?: string; code?: string } } } }
  return err?.response?.data?.error?.message ?? err?.response?.data?.error?.code ?? 'Ошибка'
}

interface DeleteBlock {
  lead_of_projects: { id: string; name: string }[]
  creator_of_projects: { id: string; name: string }[]
  reporter_of_tasks: { id: string; title: string; project_id: string }[]
}

function extractDeleteBlock(e: unknown): DeleteBlock | null {
  const err = e as {
    response?: {
      data?: {
        error?: {
          code?: string
          lead_of_projects?: { id: string; name: string }[]
          creator_of_projects?: { id: string; name: string }[]
          reporter_of_tasks?: { id: string; title: string; project_id: string }[]
        }
      }
    }
  }
  const ed = err?.response?.data?.error
  if (ed?.code !== 'user_has_active_references') return null
  return {
    lead_of_projects: ed.lead_of_projects ?? [],
    creator_of_projects: ed.creator_of_projects ?? [],
    reporter_of_tasks: ed.reporter_of_tasks ?? [],
  }
}

/** Русские числовые окончания: 1 проект, 2 проекта, 5 проектов. */
function ruEnding(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod100 >= 11 && mod100 <= 14) return many
  if (mod10 === 1) return one
  if (mod10 >= 2 && mod10 <= 4) return few
  return many
}
